import React, { useEffect, useState } from "react";
import { Table, Card, Button, Tag, Empty, Spin, Popconfirm } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePen, faTrash, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
// REDUX
import {
  useLazyGetOrganizationsQuery,
  useDeleteOrganizationMutation,
} from "../../../redux/organizations/organizationAPI";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// INTERFACES
import { OrganizationInterface } from "../../../interfaces/Organizations.interface";
// FLATBUFFER DISPLAY
import { formatUuidT, orgTypeLabel } from "../../../flatbuffers/display";
import { OrgType } from "../../../flatbuffers/generated/archive/org-type";
// STORE
import { RootState } from "../../../app/store";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// AUTHORIZATION
import { useScreenPermission } from "../../../customHooks/useScopedPermission";
import UnauthorizedPage from "../../unauthorized/UnauthorizedPage";

export default function OrganizationDraftsPage() {
  const { theme, globalOrganizationId } = useSelector((state: RootState) => state.home);
  const navigate = useNavigate();
  // Reads SCREEN_PERMISSION["drafts"] -- the same declaration Layout.tsx's
  // sidebar link reads. Previously this page had no self-check of its own
  // (see the "drafts" comment in screenPermissions.ts); the sidebar hiding
  // the link was the only enforcement, so a direct URL visit bypassed it
  // for any authenticated user regardless of permission.
  const allowed = useScreenPermission("drafts");

  const [drafts, setDrafts] = useState<OrganizationInterface[]>([]);

  const [triggerGetOrgs, { data, isLoading, isFetching }] = useLazyGetOrganizationsQuery();

  const [triggerDeleteOrg, { isLoading: isDeleting, isSuccess: isDeleteSuccess }] =
    useDeleteOrganizationMutation();

  const fetchDrafts = async () => {
    const token = await GETJwtToken();
    triggerGetOrgs({
      page: 1,
      limit: 100,
      token,
      parentId: globalOrganizationId,
      searchAndFilterString: "",
      statusFilter: "DRAFT",
    });
  };

  useEffect(() => {
    if (globalOrganizationId) {
      fetchDrafts();
    }
  }, [globalOrganizationId]);

  useEffect(() => {
    if (data && data.data) {
      setDrafts(data.data);
    }
  }, [data]);

  useEffect(() => {
    if (isDeleteSuccess) {
      fetchDrafts();
    }
  }, [isDeleteSuccess]);

  const handleDelete = async (id: string) => {
    const token = await GETJwtToken();
    triggerDeleteOrg({ id, token });
  };

  const handleResume = (orgId: string) => {
    navigate(`/organization-management/setup-wizard?orgId=${orgId}`);
  };

  const handleDeleteRecord = (record: OrganizationInterface) => {
    handleDelete(formatUuidT(record.id));
  };

  const handleResumeRecord = (record: OrganizationInterface) => {
    handleResume(formatUuidT(record.id));
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text: OrgType) => (
        <Tag color={text === OrgType.ORG ? "blue" : "orange"}>{orgTypeLabel(text)}</Tag>
      ),
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: string) => (text ? new Date(text).toLocaleDateString() : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: OrganizationInterface) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="primary"
            size="small"
            icon={<FontAwesomeIcon icon={faArrowRight} />}
            onClick={() => handleResumeRecord(record)}
          >
            Resume
          </Button>
          <Popconfirm
            title="Delete this draft?"
            onConfirm={() => handleDeleteRecord(record)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button danger size="small" icon={<FontAwesomeIcon icon={faTrash} />}>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className={`layout__contentPage__${theme}`}>
      {allowed ? (
        <Card style={{ margin: "16px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <h2 style={{ margin: 0 }}>
              <FontAwesomeIcon icon={faFilePen} style={{ marginRight: 8 }} />
              Organization Drafts
            </h2>
            <Button
              type="primary"
              onClick={() => navigate("/organization-management/setup-wizard")}
            >
              + New Organization
            </Button>
          </div>
          <Spin spinning={isLoading || isFetching || isDeleting}>
            {drafts.length === 0 && !isLoading && !isFetching ? (
              <Empty description="No draft organizations" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Table
                dataSource={drafts}
                className="my-custom-table"
                columns={columns}
                rowKey={(record) => formatUuidT(record.id)}
                size="small"
                pagination={false}
                rowClassName={(_, index) => getTableRowClass(index, theme)}
              />
            )}
          </Spin>
        </Card>
      ) : (
        <UnauthorizedPage />
      )}
    </div>
  );
}
